// @flow
import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { isUndefined } from 'lodash';

import { post } from 'api';
import { errorResponseFormatter } from 'utils/apiHelpers';
import { Alert, Col, LinkButton } from 'bs';
import { Input } from 'components/FormikFields';
import Icon from 'components/Icon';
import PageSpinner from 'components/PageSpinner';
import SubmitButton from 'components/SubmitButton';
import CancelButton from 'components/CancelButton';
import Checkbox from 'components/Checkbox';

import PasswordReqs from './PasswordReqs';
import { termsLabel, termsError } from './TermsAndPrivacyCheck';
import { type Meta } from './types';
import { CenterBox } from './utils';

const InvalidTokenMessage = () => {
    return (
        <CenterBox>
            <div>
                <h2>This single-use page has expired.</h2>
                <p>
                    Please request a new{' '}
                    <Link to='/reset-password'>password reset</Link> to try again.
                </p>
            </div>
        </CenterBox>
    );
};

function validatePassword(values) {
    const errors = {};

    if (!values.password) {
        errors.password = 'Required.';
    }

    if (values.password_rpt !== values.password) {
        errors.password_rpt = 'Match up to make certain you know your new password.';
    }

    if (values.agree !== true) {
        errors.agree = termsError;
    }

    return errors;
}

const resetPassword = (id, token, password, callbacks) => {
    const path = `user/reset/${id}/${token}/`;
    callbacks.start();
    post(path, { password })
        .then(data => callbacks.done())
        .catch(error => callbacks.fail(error));
};

const ERROR_429 = 'Too many reset attempts. Please wait and try again later.';

const ThrottleErrorMessage = props => {
    return (
        <CenterBox>
            <Alert flavor='danger'>{ERROR_429}</Alert>
        </CenterBox>
    );
};

type Props = {
    meta: Meta,
    id: string,
    token: string,
    goHome: () => void,
};

class ResetPassword extends Component<Props> {
    state = {
        // initial state for validating url token
        validToken: undefined,
        throttleError: false,
        // state for managing reset form
        submittingReset: false,
        resetComplete: false,
    };

    componentDidMount() {
        const { id, token } = this.props;
        const path = `user/check-reset/${id}/${token}/`;
        post(path)
            .then(() => {
                this.setState({
                    validToken: true,
                    throttleError: false,
                });
            })
            .catch(error => {
                this.setState({
                    validToken: false,
                    throttleError: error.status === 429,
                });
            });
    }

    render() {
        const {
            throttleError,
            validToken,
            submittingReset,
            resetComplete,
        } = this.state;
        const { id, token, goHome } = this.props;

        const setComplete = () => this.setState({ resetComplete: true });

        if (isUndefined(validToken) || submittingReset) {
            return (
                <CenterBox>
                    <PageSpinner />
                </CenterBox>
            );
        }

        if (resetComplete) {
            return (
                <CenterBox>
                    <Col>
                        <h2 className='text-success'>
                            <Icon name='check' className='mr-2' /> Success!
                        </h2>
                        <p>Please login with your new password.</p>
                        <p className='mb-0'>
                            <LinkButton flavor='success' to='/'>
                                Login
                            </LinkButton>
                        </p>
                    </Col>
                </CenterBox>
            );
        }

        if (throttleError) {
            return <ThrottleErrorMessage />;
        }

        if (!validToken) {
            return <InvalidTokenMessage />;
        }

        const form = (
            <Formik
                initialValues={{ password: '', password_rpt: '' }}
                validate={values => validatePassword(values)}
                onSubmit={({ password }, actions) => {
                    const callbacks = {
                        start: () => {
                            actions.setSubmitting(true);
                            actions.setStatus(null);
                        },
                        done: () => {
                            actions.setSubmitting(false);
                            setComplete();
                        },
                        fail: error => {
                            let message = 'Correct errors below.';
                            actions.setSubmitting(false);
                            if (error.status === '429') {
                                message = ERROR_429;
                            } else {
                                actions.setErrors(errorResponseFormatter(error.json));
                            }
                            actions.setStatus(message);
                        },
                    };

                    resetPassword(id, token, password, callbacks);
                }}
            >
                {({
                    errors,
                    isValid,
                    status,
                    isSubmitting,
                    values,
                    setFieldValue,
                    touched,
                }) => (
                    <Form>
                        <h2>Set your password</h2>
                        {status && <Alert flavor='danger'>{status}</Alert>}
                        <Input
                            type='password'
                            name='password'
                            label='Password'
                            autoComplete='new-password'
                            helpText={
                                <PasswordReqs
                                    reset={true}
                                    className={
                                        status && errors['password']
                                            ? 'text-danger'
                                            : ''
                                    }
                                />
                            }
                        />
                        <Input
                            type='password'
                            name='password_rpt'
                            label='Repeat Password'
                            autoComplete='new-password'
                        />
                        <Checkbox
                            id='agree'
                            checked={values.agree}
                            onChange={() => setFieldValue('agree', !values.agree)}
                            label={termsLabel}
                            error={touched.agree ? errors.agree : null}
                            className='d-block mb-3'
                        />
                        <SubmitButton disabled={!isValid} submitting={isSubmitting} />
                        <CancelButton onClick={goHome} />{' '}
                    </Form>
                )}
            </Formik>
        );

        return <CenterBox>{form}</CenterBox>;
    }
}

export default ResetPassword;
